import React, { createContext, useContext, useMemo } from 'react';
import { useSelector } from 'react-redux';
import PhoneRequiredModal from '../modals/PhoneRequiredModal';

const PLACEHOLDER_PHONE = '0000000000';

/**
 * Contexte pour gérer la validation du numéro de téléphone.
 * Affiche automatiquement une modale bloquante si le client connecté
 * a un numéro de téléphone placeholder (0000000000).
 */
export const PhoneValidationCtx = createContext({
  needsPhone: false,
});

/**
 * Hook pour accéder au contexte de validation du téléphone.
 */
export const usePhoneValidation = () => useContext(PhoneValidationCtx);

/**
 * Provider qui surveille le client Redux et affiche la modale si nécessaire.
 */
const PhoneValidationProvider = ({ children }) => {
  const client = useSelector((state) => state.client);

  // Détermine si le client a besoin de renseigner son téléphone
  const needsPhone = useMemo(() => {
    // Pas de client connecté = pas de modale
    if (!client || !client.id) {
      return false;
    }

    // Tant que le téléphone réel n'est pas chargé (undefined/null), on ne bloque pas.
    if (!client.tel) {
      return false;
    }

    // Client avec un téléphone différent du placeholder = pas de modale
    if (client.tel !== PLACEHOLDER_PHONE) {
      return false;
    }

    // Client connecté avec téléphone placeholder = modale
    return true;
  }, [client]);

  const contextValue = useMemo(
    () => ({
      needsPhone,
    }),
    [needsPhone],
  );

  return (
    <PhoneValidationCtx.Provider value={contextValue}>
      {children}
      <PhoneRequiredModal
        open={needsPhone}
        onUpdated={() => {
          // La modale se fermera automatiquement car le client sera mis à jour
          // dans le store Redux, ce qui recalculera needsPhone = false
        }}
      />
    </PhoneValidationCtx.Provider>
  );
};

export default PhoneValidationProvider;
